import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// ─── Lease key dates (Settings → Fixed costs, owner-only) ─────────────────
// CJ 2026-07-22. Renewal option, notice-to-vacate, lease end, rent review —
// the deadlines you can't afford to miss. Shown with days-remaining and a
// colour so an approaching one jumps out. Backend: /api/admin/lease-dates.

const KINDS = [
  { key: 'renewal_option', label: 'Renewal option' },
  { key: 'notice_to_vacate', label: 'Notice to vacate' },
  { key: 'lease_end', label: 'Lease end' },
  { key: 'rent_review', label: 'Rent review' },
  { key: 'other', label: 'Other' },
];
const kindLabel = (k) => KINDS.find((x) => x.key === k)?.label || String(k || '').replace(/_/g, ' ');
const dayOf = (d) => (d || '').slice(0, 10);
function daysUntil(d) {
  const due = new Date(`${dayOf(d)}T00:00:00`);
  const today = new Date(new Date().toDateString());
  return Math.round((due - today) / 86400000);
}
const colorFor = (days) => (days < 0 ? '#9e9e9e' : days <= 30 ? '#b91c1c' : days <= 90 ? '#bf6c00' : '#2e7d32');
const awayLabel = (days) => (days < 0 ? `${-days}d ago` : days === 0 ? 'today' : `${days}d`);

export function LeaseDatesView({ currentUser, locations }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState('');

  const blank = () => ({ location_id: locations[0]?.id || '', kind: 'renewal_option', label: '', due_date: '', notes: '' });

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', '/api/admin/lease-dates', null, headers);
      setRows(Array.isArray(r?.lease_dates) ? r.lease_dates : []);
    } catch { setRows([]); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (p) => setDraft((d) => ({ ...d, ...p }));
  function openNew() { setEditing('new'); setDraft(blank()); setMsg(''); }
  function openEdit(r) {
    setEditing(r.id);
    setDraft({ location_id: r.location_id, kind: r.kind, label: r.label || '', due_date: dayOf(r.due_date), notes: r.notes || '' });
    setMsg('');
  }
  function close() { setEditing(null); setDraft(null); setMsg(''); }

  async function save() {
    if (!draft) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.due_date)) { setMsg('Pick a date.'); return; }
    setMsg('Saving…');
    const payload = { location_id: Number(draft.location_id), kind: draft.kind, label: draft.label.trim(), due_date: draft.due_date, notes: draft.notes.trim() };
    try {
      if (editing === 'new') await api('POST', '/api/admin/lease-dates', payload, headers);
      else await api('PUT', `/api/admin/lease-dates/${editing}`, payload, headers);
      close(); load();
    } catch (e) { setMsg(String(e?.message || e).slice(0, 200)); }
  }
  async function remove(r) {
    if (!window.confirm(`Delete ${kindLabel(r.kind)} for ${r.location_name}?`)) return;
    try { await api('DELETE', `/api/admin/lease-dates/${r.id}`, null, headers); load(); } catch { /* keep */ }
  }

  const input = 'border border-gray-300 rounded px-2 py-1 text-sm';

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="text-base font-semibold">Lease key dates</div>
          <div className="text-xs text-gray-500">Renewal options, notice deadlines, lease ends — with days remaining so nothing lapses.</div>
        </div>
        {!editing && <button onClick={openNew} className="btn-primary">Add date</button>}
      </div>

      {editing && draft && (
        <div className="mt-3 p-3 rounded" style={{ background: '#faf8f4' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <label className="text-xs text-gray-600">Café
              <select className={input + ' block mt-0.5'} value={draft.location_id} onChange={(e) => upd({ location_id: e.target.value })}>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">Type
              <select className={input + ' block mt-0.5'} value={draft.kind} onChange={(e) => upd({ kind: e.target.value })}>
                {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">Date
              <input className={input + ' block mt-0.5'} type="date" value={draft.due_date} onChange={(e) => upd({ due_date: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600" style={{ flex: 1, minWidth: 160 }}>Note
              <input className={input + ' block w-full mt-0.5'} value={draft.notes} placeholder="e.g. must give written notice by this date" onChange={(e) => upd({ notes: e.target.value })} />
            </label>
          </div>
          <div className="mt-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={save} className="btn-primary">Save</button>
            <button onClick={close} className="text-sm text-gray-600 underline">Cancel</button>
            {msg && <span className="text-xs" style={{ color: msg === 'Saving…' ? '#5a6470' : '#b91c1c' }}>{msg}</span>}
          </div>
        </div>
      )}

      <div className="mt-3">
        {loading ? <div className="text-sm text-gray-500">Loading…</div>
          : rows.length === 0 ? <div className="text-sm text-gray-500">No lease dates yet. Add your renewal-option and notice deadlines.</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', color: '#757575' }}>
                      <th style={{ padding: 6 }}>Café</th>
                      <th style={{ padding: 6 }}>Type</th>
                      <th style={{ padding: 6 }}>Date</th>
                      <th style={{ padding: 6, textAlign: 'right' }}>Away</th>
                      <th style={{ padding: 6 }}>Note</th>
                      <th style={{ padding: 6 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const days = daysUntil(r.due_date);
                      return (
                        <tr key={r.id} style={{ borderBottom: '1px solid #f2f2f2' }}>
                          <td style={{ padding: 6 }}>{r.location_name || `Location ${r.location_id}`}</td>
                          <td style={{ padding: 6 }}>{kindLabel(r.kind)}{r.label ? ` · ${r.label}` : ''}</td>
                          <td style={{ padding: 6, whiteSpace: 'nowrap' }}>{dayOf(r.due_date)}</td>
                          <td style={{ padding: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 600, color: colorFor(days) }}>{awayLabel(days)}</span>
                          </td>
                          <td style={{ padding: 6, color: '#5a6470' }}>{r.notes || <span className="text-gray-400">—</span>}</td>
                          <td style={{ padding: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button onClick={() => openEdit(r)} className="text-xs underline text-gray-600 mr-2">Edit</button>
                            <button onClick={() => remove(r)} className="text-xs underline" style={{ color: '#b91c1c' }}>Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
      </div>
    </div>
  );
}
